/**
 * Service worker registration + Web Push subscription.
 *
 * ensureServiceWorker() — registers /sw.js at app boot. Idempotent.
 * subscribeToPush()     — called when the operator grants permission.
 *                         Sends the subscription JSON to the worker API.
 *
 * iOS 16.4+ notes:
 *   - PWA must be installed (Add to Home Screen) *and* launched from the
 *     home-screen icon at least once before a subscription will work.
 *   - Browser context (not PWA) can also subscribe on macOS Safari and iOS,
 *     but reliability is lower; we always attempt regardless.
 */

import { subscribePush as apiSubscribePush } from '../api/endpoints'

const SW_URL = '/sw.js'
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string

export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    const reg = await navigator.serviceWorker.register(SW_URL, {
      scope: '/',
    })
    return reg
  } catch (err) {
    console.error('Service worker registration failed:', err)
    return null
  }
}

export async function currentPushPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied'
  return Notification.permission
}

/**
 * Prompt (if needed), subscribe, and POST the subscription to the worker.
 * Returns true on success.
 */
export async function subscribeToPush(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false
  }
  if (!VAPID_PUBLIC_KEY) {
    console.warn('VITE_VAPID_PUBLIC_KEY not configured — cannot subscribe')
    return false
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const reg =
    (await navigator.serviceWorker.getRegistration('/')) ??
    (await ensureServiceWorker())
  if (!reg) return false

  const existing = await reg.pushManager.getSubscription()
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        VAPID_PUBLIC_KEY
      ) as BufferSource,
    }))

  const json = sub.toJSON() as PushSubscriptionJSON
  await apiSubscribePush(json)
  return true
}

export async function isSubscribed(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false
  const reg = await navigator.serviceWorker.getRegistration('/')
  if (!reg) return false
  const sub = await reg.pushManager.getSubscription()
  return !!sub
}

/** VAPID public keys are base64url-encoded — convert to Uint8Array for subscribe(). */
function urlBase64ToUint8Array(base64url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i)
  return output
}

export function isStandalonePwa(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window as unknown as { navigator: { standalone?: boolean } }).navigator
      .standalone === true
  )
}

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}
